// Lot 7 — propositions de l'IA dans « Modifier ». L'IA propose, Anissa
// décide : chaque proposition montre son « pourquoi » et les faits cités ;
// rien ne s'applique sans « Appliquer », qui devient une édition normale de
// la semaine (validation du repas à refaire, annulable dans l'éditeur).

import { useEffect, useRef, useState } from 'react';
import {
  MESSAGE_MGD_PROPOSITIONS, MESSAGE_STATUT_ILLISIBLE, TYPES_PROPOSITION, appliquerEtEnregistrer, etatPropositionsMgd, proposerAjustements,
} from './services/suiviPropositions';

function titreProposition(p) {
  if (p.kind === 'remplacer_alternative') return `${p.repas} · remplacer « ${p.cibleTitre} » par « ${p.nouveau.title} »`;
  if (p.kind === 'ajouter_alternative') return `${p.repas} · ajouter « ${p.nouveau.title} »`;
  if (p.kind === 'modifier_recette') return `${p.repas} · recette de « ${p.cibleTitre} »`;
  return `${p.repas} · description de « ${p.cibleTitre} »`;
}

/** Propositions fermées : programme MGD, ou statut illisible (à relire). */
export function EtatFerme({ etat, onReessayer }) {
  if (etat === 'mgd') return <p className="jrn-block__intro" role="status">{MESSAGE_MGD_PROPOSITIONS}</p>;
  if (etat !== 'illisible') return null;
  return (
    <p className="jrn-block__intro" role="status">
      {MESSAGE_STATUT_ILLISIBLE}{' '}
      <button type="button" className="jrn-btn jrn-btn--ghost" onClick={onReessayer}>Réessayer</button>
    </p>
  );
}

export default function PropositionsIA({
  client, ligne, rdv = null, donnees = null, depuis = null, motif = null,
  menusModifies = false, onAppliquer, lireLigne = null, proposer = proposerAjustements, verifierOuverture = etatPropositionsMgd,
}) {
  const [etat, setEtat] = useState({ statut: 'repos', propositions: [], rejetees: [], message: null });
  const [suite, setSuite] = useState({});
  // Programme MGD (ou statut illisible) : pas de bouton, aucun appel à l'IA.
  const [ouverture, setOuverture] = useState({ cle: null, etat: null });
  const [essai, setEssai] = useState(0);
  const clientId = client?.id || null;
  const cle = `${clientId}|${essai}`;
  useEffect(() => {
    let vivant = true;
    const cleLue = `${clientId}|${essai}`;
    Promise.resolve()
      .then(() => verifierOuverture(clientId))
      .catch(() => 'illisible')
      .then((resultat) => {
        const lu = ['ouvert', 'mgd'].includes(resultat) ? resultat : 'illisible';
        if (vivant) setOuverture({ cle: cleLue, etat: lu });
      });
    return () => { vivant = false; };
  }, [clientId, essai, verifierOuverture]);
  const etatOuverture = ouverture.cle === cle ? ouverture.etat : null;
  const ouvert = etatOuverture === 'ouvert';
  // Une seule application à la fois : la suivante part de la ligne enregistrée.
  const [application, setApplication] = useState(null);
  const verrou = useRef(false);

  async function demander() {
    setEtat({ statut: 'chargement', propositions: [], rejetees: [], message: null });
    setSuite({});
    try {
      const r = await proposer({ client, ligne, rdv, donnees, depuis, motif });
      setEtat({ statut: 'ok', propositions: r.propositions, rejetees: r.rejetees, message: null });
    } catch (error) {
      setEtat({ statut: 'erreur', propositions: [], rejetees: [], message: error?.message || 'Les propositions n’ont pas pu être préparées.' });
    }
  }

  async function appliquer(p) {
    if (verrou.current) return;
    verrou.current = true;
    setApplication(p.id);
    setSuite((s) => ({ ...s, [p.id]: { enCours: true } }));
    let r;
    try {
      // La ligne la plus récente, file d'enregistrement vidée : une
      // application précédente n'est jamais écrasée.
      const base = lireLigne ? await lireLigne() : ligne;
      r = base
        ? await appliquerEtEnregistrer({
          client, ligne: base, proposition: p, menusModifies, enregistrer: (c, o) => onAppliquer?.(c, o),
        })
        : { ok: false, error: 'Tes dernières modifications ne sont pas encore enregistrées : réessaie dans un instant.' };
    } catch (e) {
      r = { ok: false, error: e?.message || 'Modification non enregistrée.' };
    } finally {
      verrou.current = false;
      setApplication(null);
    }
    setSuite((s) => ({ ...s, [p.id]: r.ok ? { applique: true } : { erreur: r.error } }));
  }

  return (
    <section className="jrn-block suivi-propositions">
      <div className="jrn-block__head">
        <h3 className="jrn-block__title">Propositions de l’IA</h3>
        {ouvert && (
          <button type="button" className="jrn-btn jrn-btn--soft" disabled={etat.statut === 'chargement'} onClick={() => demander()}>
            {etat.statut === 'chargement' ? 'Préparation…' : (etat.statut === 'repos' ? 'Proposer des ajustements' : 'Proposer à nouveau')}
          </button>
        )}
      </div>
      <EtatFerme etat={etatOuverture} onReessayer={() => setEssai((n) => n + 1)} />
      {ouvert && (
        <p className="jrn-meta-chip">
          Tes notes et les mots de la cliente partent à l’IA sans son nom ni ses coordonnées. Retire les noms d’autres personnes si besoin.
        </p>
      )}
      <p className="jrn-block__intro">
        Des idées de menus et de recettes tirées des faits ci-dessus. Tu décides : rien ne change sans « Appliquer ».
      </p>
      {etat.statut === 'erreur' && <p className="jrn-error" role="alert">{etat.message}</p>}
      {etat.statut === 'ok' && etat.propositions.length === 0 && <p>Aucune proposition retenue.</p>}
      {etat.propositions.map((p) => {
        const s = suite[p.id] || {};
        if (s.ecartee) return null;
        return (
          <article key={p.id} className="jrn-surface suivi-proposition">
            <h4 className="jrn-label">{titreProposition(p)}</h4>
            <p className="jrn-meta-chip">{TYPES_PROPOSITION[p.kind]}</p>
            {p.nouveau.description && <p>Description : {p.nouveau.description}</p>}
            {p.nouveau.ingredients.length > 0 && <p>Ingrédients : {p.nouveau.ingredients.join(' · ')}</p>}
            {p.nouveau.preparation.length > 0 && <p>Préparation : {p.nouveau.preparation.join(' · ')}</p>}
            <p><strong>Pourquoi :</strong> {p.pourquoi}</p>
            {p.etapesConservees && <p className="jrn-meta-chip">Étapes d’origine conservées, à relire</p>}
            {p.aVerifier && <p className="jrn-error">{p.aVerifier}</p>}
            <ul className="suivi-proposition__faits">
              {p.preuves.map((fait) => <li key={fait.id}>{fait.id} · {fait.texte}</li>)}
            </ul>
            {s.erreur && <p className="jrn-error" role="alert">{s.erreur}</p>}
            {s.applique ? (
              <p role="status">Appliquée : à relire et valider dans « Semaine, menus & recettes ».</p>
            ) : (
              <div className="jrn-actions">
                <button type="button" className="jrn-btn jrn-btn--primary" disabled={application !== null} onClick={() => appliquer(p)}>
                  {s.enCours ? 'Application…' : 'Appliquer'}
                </button>
                <button type="button" className="jrn-btn jrn-btn--ghost" disabled={s.enCours} onClick={() => setSuite((x) => ({ ...x, [p.id]: { ecartee: true } }))}>
                  Écarter
                </button>
              </div>
            )}
          </article>
        );
      })}
      {etat.rejetees.length > 0 && (
        <details>
          <summary>{etat.rejetees.length} proposition{etat.rejetees.length > 1 ? 's' : ''} écartée{etat.rejetees.length > 1 ? 's' : ''} automatiquement</summary>
          <ul>{etat.rejetees.map((r) => <li key={r.id}>{r.id} : {r.raison}</li>)}</ul>
        </details>
      )}
    </section>
  );
}
